/**
 * useInProgressEnrichment — derives EnrichedInProgressItem[] from profile.inProgress.
 *
 * Shared between CommunityProfilePage and CommunityProfileUserView.
 *
 * Hydration tier: the upstream queries (GET_PROFILE_IN_PROGRESS_CAROUSEL,
 * GET_IN_PROGRESS_FULL) intentionally do NOT select
 * `featureMedia.mediaAssets.aiMetadata.learnerLevel` (PR 2 dropped it to
 * keep the ai-service tail off the critical path). To restore the
 * difficulty pill, this hook reaches into `useContentHydration` for each
 * surviving entry's `{contentId, contentType}` titleGroupId and merges
 * the hydrated `featureMedia` (which DOES carry `mediaAssets.aiMetadata`)
 * onto the embedded `featured` content before tile construction. The
 * embedded entry stays the source of `userMediaPosition` — that overlay
 * is applied after `buildTileData` runs, exactly as before.
 *
 * Return shape: `{ items, isHydrating, overallProgress }`.
 *  - `items` is the stabilized enriched array (ref-stable across Apollo
 *    re-emissions that don't change the visible content — see the
 *    `stableRef` note below). Same-series episodes collapse to a single
 *    tile via the dedup gate.
 *  - `isHydrating` reflects the *internal* `useTitleGroupHydration` window
 *    only. Callers must OR this with their own upstream-query loading
 *    flag (`GET_PROFILE_IN_PROGRESS_CAROUSEL.loading`,
 *    `GET_IN_PROGRESS_FULL.loading`, etc.) — this hook has no visibility
 *    into whether the carousel query itself is still in flight.
 *    See `useCollectionsData` for the canonical combination.
 *  - `overallProgress` is `round(100 * sum(position) / sum(duration))`
 *    across every in-progress media position that passes the filters
 *    (hidden / position=0 / >=95% complete), summed BEFORE the dedup
 *    gate so multiple in-progress episodes of one series each contribute
 *    their own position+duration. The carousel collapses duplicates
 *    visually; the stat reflects the user's full in-progress watch state.
 *    `0` when there's no usable duration to divide by.
 */

import { useMemo, useRef } from 'react'
import _get from 'lodash/get'
import {
  buildTileData,
  buildEpisodeOfSeriesTile,
  toImmutableTile,
} from './content-utils'
import useTitleGroupHydration from './useTitleGroupHydration'
import type {
  InProgressItem,
  EnrichedInProgressItem,
} from '../../types'

interface UseInProgressEnrichmentResult {
  items: EnrichedInProgressItem[]
  isHydrating: boolean
  overallProgress: number
}

export default function useInProgressEnrichment (
  inProgress: InProgressItem[] | undefined,
  language?: string,
  jwt?: string,
): UseInProgressEnrichmentResult {
  // Collect hydration tuples — only for entries whose featured content has
  // both contentId + contentType (deriveTitleGroupId requires both).  The
  // hydration hook dedupes internally, but pre-filtering keeps the dep
  // array stable across renders that drop hidden / completed items.
  const hydrationItems = useMemo(() => {
    if (!inProgress) return []
    const seen = new Set<string>()
    return inProgress.reduce<Array<{ contentId: string; contentType: string }>>(
      (acc, item) => {
        const featured = _get(item, 'media.contentFeatured[0]', null)
        const cid = featured?.contentId != null ? String(featured.contentId) : ''
        const ctype = featured?.contentType ? String(featured.contentType) : ''
        if (!cid || !ctype) return acc
        const key = `${cid}-${ctype}`
        if (seen.has(key)) return acc
        seen.add(key)
        acc.push({ contentId: cid, contentType: ctype })
        return acc
      },
      [],
    )
  }, [inProgress])

  // Per-row hydration via useTitleGroupHydration. Currently Watching is
  // always a short list (max ~10 items) so the default fanoutThreshold of
  // 24 keeps everything on slot 0.
  const titleGroupIds = useMemo(
    () => hydrationItems.map(({ contentId, contentType }) => `${contentId}-${contentType}`),
    [hydrationItems],
  )
  const { contentMap, loading: titleGroupHydrating } = useTitleGroupHydration(
    titleGroupIds,
    language || 'en',
    { skip: !language || titleGroupIds.length === 0, jwt },
  )

  const { items: enriched, overallProgress: computedOverallProgress } = useMemo(() => {
    const rawInProgress = inProgress || []
    // Dedup key set: collapses multiple in-progress media positions that
    // resolve to the same visible tile into a single row. For an episode
    // pivoted via `buildEpisodeOfSeriesTile`, `tileData.contentId` is the
    // PARENT SERIES contentId — so two episodes of the same series share
    // a key and collapse to one tile (matching Home's Continue Watching
    // UX). For a standalone in-progress video (no parent series),
    // `tileData.contentId` is the video's own id, so distinct videos keep
    // their own tiles. First-occurrence wins, matching the dedup behavior
    // in `enrichPlaylistItems` (content-utils.ts:303).
    const seenTileIds = new Set<string>()
    // Index of `acc` per dedup key so a second/Nth episode of an already-
    // pivoted series can `.push()` its contentId + mediaId onto the
    // surviving tile's bundle arrays instead of being dropped on the floor.
    // Without this map, `handleRemoveInProgressItem` only knows the FIRST
    // episode's ids and can only un-flag one server row — sibling episodes
    // stay in `UserProfile.inProgress` and the series tile re-pivots on
    // the next render (the bug this map fixes).
    const accIndexByTileKey = new Map<string, number>()
    // Running totals for the time-weighted `overallProgress` stat.
    // Accumulated BEFORE the dedup gate so duplicate-series episodes each
    // contribute their own position+duration — the "{percent}% overall"
    // label summarizes the user's full in-progress watch state across
    // the raw 24-most-recent list, not just the visible tile set.
    let totalPosition = 0
    let totalDuration = 0
    const items = rawInProgress.reduce((acc: EnrichedInProgressItem[], item) => {
      if (!item || !item.media) return acc
      if (item.hiddenByUser || item.hiddenFromContinue) return acc
      const featured = _get(item, 'media.contentFeatured[0]', null)
      if (!featured) return acc
      const duration = _get(item, 'media.duration', 0)
      const position = Number(item.position) || 0
      if (position === 0) return acc
      const percentComplete = duration > 0
        ? Math.min(100, Math.round((position / duration) * 100))
        : 0
      if (percentComplete >= 95) return acc
      // Time-weighted total: contribute BEFORE the dedup gate. Hidden /
      // zero / >=95% items are excluded by the filters above and never
      // reach here, matching what the carousel surfaces.
      totalPosition += position
      totalDuration += duration
      const assetUrl = _get(featured, 'contentAssetReferences[0].asset.renderUrl', '')
      const legacyUrl = _get(featured, 'imageContentRelationships[0].image.url', '')
      const mediaId = _get(item, 'media.mediaId', item.mediaId)
      // When the hydrated content is available, MERGE it with `featured`
      // rather than swapping outright. `featured` (from
      // GET_PROFILE_IN_PROGRESS_CAROUSEL.media.contentFeatured) already
      // carries the full `contentSeries` shape (contentType, studio,
      // subType, displayType, contentAnalytic, references). Pre-hydration
      // renders use that and produce a series-shaped tile when applicable.
      // Hydration's value-add is `featureMedia.mediaAssets.aiMetadata.learnerLevel`
      // for the difficulty pill — that's the field we want from
      // `hydrated`. Prefer `featured.contentSeries` over `hydrated.contentSeries`
      // so the series shape never regresses across re-paints, even if a
      // future hydration query returns a sparse series projection.
      // Fixes series-tag flip on /library carousels.
      const hydrationKey = featured?.contentId && featured?.contentType
        ? `${String(featured.contentId)}-${String(featured.contentType)}`
        : ''
      const hydrated = hydrationKey ? contentMap.get(hydrationKey) : undefined
      const tileSource = hydrated
        ? {
          ...featured,
          ...hydrated,
          contentSeries: featured?.contentSeries ?? hydrated?.contentSeries,
        }
        : featured
      // Pivot episodes-of-series to a series-shaped tile (matches Home's
      // Continue Watching V3 data shape so the hover renders the Gaia|Original
      // badge, content-type tag, and series labels). Falls back to a plain
      // video tile when there's no series.
      const tileData = buildEpisodeOfSeriesTile(tileSource, {
        userPosition: position,
        mediaDuration: duration,
        mediaId,
      }) ?? buildTileData(tileSource)
      if (tileData && !tileData.featuredContent) {
      // Non-pivoted (no parent series) — keep legacy behavior of stamping
      // userMediaPosition onto the tile's own featureMedia for progress UI.
        tileData.featureMedia = {
          duration,
          mediaId,
          userMediaPosition: { position },
        }
      }
      // Dedup gate: drop subsequent in-progress entries whose tile resolves
      // to a tile identity already shown. Done AFTER tile build because the
      // dedup key is `tileData.contentId`, which is the SERIES id for
      // pivoted episodes (the whole point of the collapse). Items without
      // a usable tile key fall through to keep their own row.
      //
      // On dedup-hit, we still need to remember THIS episode's contentId +
      // mediaId so the Remove handler can fan out `flagWatchedVideo` /
      // cache.modify across every sibling — append the current entry's ids
      // onto the surviving tile's bundle arrays before returning.
      // Standalone videos (no tileKey) skip the bundle bookkeeping; the
      // Remove handler falls back to `[item.contentId]` / `[item.mediaId]`
      // when the bundle arrays are absent.
      const tileKey = tileData ? String(tileData.contentId ?? '') : ''
      const episodeContentIdNum = (() => {
        const raw = featured?.contentId
        if (raw == null) return null
        const n = Number(raw)
        return Number.isFinite(n) && n > 0 ? n : null
      })()
      if (tileKey && seenTileIds.has(tileKey)) {
        const existingIdx = accIndexByTileKey.get(tileKey)
        if (existingIdx != null) {
          const surviving = acc[existingIdx]
          if (episodeContentIdNum != null && surviving.bundledEpisodeContentIds) {
            surviving.bundledEpisodeContentIds.push(episodeContentIdNum)
          }
          if (item.mediaId && surviving.bundledEpisodeMediaIds) {
            surviving.bundledEpisodeMediaIds.push(item.mediaId)
          }
        }
        return acc
      }
      if (tileKey) {
        seenTileIds.add(tileKey)
        accIndexByTileKey.set(tileKey, acc.length)
      }
      acc.push({
        id: item.mediaId,
        mediaId: item.mediaId,
        title: featured.title || '',
        path: featured.path || '',
        contentId: featured.contentId || null,
        percentComplete,
        imageUrl: assetUrl || legacyUrl || '',
        isAssetUrl: !!assetUrl,
        duration,
        position,
        tileData,
        tileDataImmutable: tileData ? toImmutableTile(tileData) : null,
        bundledEpisodeContentIds: episodeContentIdNum != null ? [episodeContentIdNum] : [],
        bundledEpisodeMediaIds: item.mediaId ? [item.mediaId] : [],
      })
      return acc
    }, [])
    const overallProgress = totalDuration > 0
      ? Math.round((100 * totalPosition) / totalDuration)
      : 0
    return { items, overallProgress }
  }, [inProgress, contentMap])

  // Stabilize the returned reference across Apollo re-emissions that don't
  // change the visible content.  GET_PROFILE_IN_PROGRESS_CAROUSEL selects
  // `media.contentFeatured` which dereferences normalized `Content:<id>`
  // refs — so every `GET_CONTENT` hydration write (My Playlists, Watch
  // History, Followed Playlists) bumps `inProgress`'s reference even when
  // the per-item playback state is identical.  Without this stabilization
  // each Apollo re-emit produces a new enriched array, defeating
  // `React.memo` on `SystemPlaylistRow` and causing the Currently Watching
  // row to re-render with byte-identical data (~4 spurious renders per
  // cold load, observed via `[system-row]` diagnostics).
  //
  // Signature includes `contentMap.size` so a hydration that adds new
  // `Content:<id>` entries (restoring the difficulty pill) still flushes
  // through — pill-bearing tiles get a fresh ref on the same tick they
  // first have hydrated data to paint with. `overallProgress` is rolled
  // into the signature so the stat re-renders when the time-weighted
  // total shifts (e.g. when a hidden item drops out or a new in-progress
  // position arrives) but stays stable across no-op re-emits.
  const stableRef = useRef<{
    items: EnrichedInProgressItem[]
    overallProgress: number
    sig: string
  }>({
    items: enriched,
    overallProgress: computedOverallProgress,
    sig: '',
  })
  const nextSig = `${enriched.length}|${enriched
    .map((i) => `${i.id}/${i.percentComplete}`)
    .join(',')}|m${contentMap.size}|p${computedOverallProgress}`
  if (nextSig !== stableRef.current.sig) {
    stableRef.current = {
      items: enriched,
      overallProgress: computedOverallProgress,
      sig: nextSig,
    }
  }
  return {
    items: stableRef.current.items,
    isHydrating: titleGroupHydrating,
    overallProgress: stableRef.current.overallProgress,
  }
}

export type { UseInProgressEnrichmentResult }
